/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminVerification\AdminVerification.jsx
 * ВЕРСИЯ: 1.0.0
 * ДАТА: 03.12.2025 14:55
 * 
 * Управление email верификацией пользователей
 */

import React, { useState, useEffect } from 'react';
import './AdminVerification.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const AdminVerification = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'pending') {
        await fetchPendingUsers();
      } else if (activeTab === 'codes') {
        await fetchCodes();
      }
      await fetchStats();
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/verification/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setPendingUsers(data.data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchCodes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/verification/codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setCodes(data.data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/verification/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleVerify = async (userId) => {
    if (!window.confirm('Верифицировать пользователя вручную?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/verification/verify/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        alert('Пользователь верифицирован');
        fetchData();
      } else {
        alert(data.error || 'Ошибка');
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const handleResend = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/verification/resend/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        alert(`Код создан: ${data.code}`);
        fetchData();
      } else {
        alert(data.error || 'Ошибка');
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm('Удалить код верификации?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/verification/codes/${codeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        fetchCodes();
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (date) => {
    return new Date(date) < new Date();
  };

  return (
    <div className="admin-verification">
      <h1 className="admin-page-title">✉️ Управление верификацией</h1>

      {/* Stats */}
      {stats && (
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.users?.total || 0}</div>
            <div className="admin-stat-label">Всего пользователей</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value" style={{color: '#4caf50'}}>{stats.users?.verified || 0}</div>
            <div className="admin-stat-label">Верифицированы</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value" style={{color: '#ffc107'}}>{stats.users?.pending || 0}</div>
            <div className="admin-stat-label">Ожидают</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.codes?.total || 0}</div>
            <div className="admin-stat-label">Всего кодов</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          👤 Ожидают верификации
        </button>
        <button
          className={`admin-tab ${activeTab === 'codes' ? 'active' : ''}`}
          onClick={() => setActiveTab('codes')}
        >
          🔑 Коды верификации
        </button>
      </div>

      {loading ? (
        <div className="admin-loading">Загрузка...</div>
      ) : (
        <div className="admin-card">
          {/* Pending Users Tab */}
          {activeTab === 'pending' && (
            <>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Имя</th>
                    <th>Роль</th>
                    <th>Регистрация</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map((user) => (
                    <tr key={user.id}>
                      <td>#{user.id}</td>
                      <td>{user.email}</td>
                      <td>{user.name || '-'}</td>
                      <td>
                        <span className="admin-badge">{user.role}</span>
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>
                        <div className="admin-actions">
                          <button
                            className="admin-btn admin-btn-success"
                            onClick={() => handleVerify(user.id)}
                            title="Верифицировать"
                          >
                            ✅
                          </button>
                          <button
                            className="admin-btn admin-btn-primary"
                            onClick={() => handleResend(user.id)}
                            title="Отправить код"
                          >
                            📧
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pendingUsers.length === 0 && (
                <div className="admin-empty">Все пользователи верифицированы 🎉</div>
              )}
            </>
          )}

          {/* Codes Tab */}
          {activeTab === 'codes' && (
            <>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Код</th>
                    <th>Тип</th>
                    <th>Истекает</th>
                    <th>Статус</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((code) => (
                    <tr key={code.id}>
                      <td>#{code.id}</td>
                      <td>{code.user_email || code.email}</td>
                      <td><code className="admin-code">{code.code}</code></td>
                      <td>{code.type}</td>
                      <td>{formatDate(code.expires_at)}</td>
                      <td>
                        {code.is_used ? (
                          <span className="admin-badge admin-badge-claimed">Использован</span>
                        ) : isExpired(code.expires_at) ? (
                          <span className="admin-badge admin-badge-expired">Истёк</span>
                        ) : (
                          <span className="admin-badge admin-badge-active">Активен</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="admin-btn admin-btn-danger"
                          onClick={() => handleDeleteCode(code.id)}
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {codes.length === 0 && (
                <div className="admin-empty">Коды не найдены</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminVerification;